import { Button, Modal, Select, Form, Input, Checkbox, message } from "antd";
import { Package, MapPin, AlertCircle, Settings } from "lucide-react";
import { useState } from "react";
import { TInventory } from "~/types/inventory";
import { InventoryStatusCode } from "~/utils/constants";
import { useGetLocations } from "~/services/common";
import { useBulkUpdateInventory } from "~/services/inventory/inventory.mutations";
import { useQueryClient } from "@tanstack/react-query";
import { BOOKING_KEYS } from "~/services/booking/keys";

interface InventoryBulkUpdateButtonProps {
  selectedInventories: TInventory[];
  onUpdateComplete?: () => void;
}

export const InventoryBulkUpdateButton: React.FC<InventoryBulkUpdateButtonProps> = ({
  selectedInventories,
  onUpdateComplete,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { data: locations } = useGetLocations();
  const { mutate: bulkUpdateInventory, isPending } = useBulkUpdateInventory();
  const queryClient = useQueryClient();

  const handleOpenModal = () => {
    if (selectedInventories.length === 0) {
      message.warning("Please select inventory items to update.");
      return;
    }
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    const { status, locationId, notes, artistName, length, width, height, packingType, swapWidthHeight } = values;

    // Only include fields that have values
    const updateData: any = {
      inventoryIds: selectedInventories.map((item) => item.id),
    };

    if (status) updateData.status = status;
    if (locationId === "") {
      updateData.locationId = null;
    } else if (locationId) {
      updateData.locationId = locationId;
    }
    if (notes) updateData.notes = notes;
    if (artistName) updateData.artistName = artistName;
    if (length) updateData.length = parseFloat(length);
    if (width) updateData.width = parseFloat(width);
    if (height) updateData.height = parseFloat(height);
    if (packingType) updateData.packingType = packingType;
    if (swapWidthHeight) updateData.swapWidthHeight = true;

    bulkUpdateInventory(updateData, {
      onSuccess: (updatedInventories: TInventory[]) => {
        message.success(
          `Successfully updated ${updatedInventories.length || selectedInventories.length} inventory items!`,
          5
        );
        // Also invalidate booking queries to refresh the detail view
        queryClient.invalidateQueries({ queryKey: [BOOKING_KEYS.BOOKING] });
        queryClient.invalidateQueries({ queryKey: [BOOKING_KEYS.BOOKINGS] });
        handleCloseModal();
        onUpdateComplete?.();
      },
      onError: (error: any) => {
        console.error("Bulk update error:", error);
        const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";
        const statusCode = error?.response?.status;

        let fullError = `Bulk update failed: ${errorMessage}`;
        if (statusCode) {
          fullError += ` (Status: ${statusCode})`;
        }
        if (error?.response?.data?.error) {
          fullError += ` - ${error.response.data.error}`;
        }

        message.error(fullError, 10);
      },
    });
  };

  const getStatusColor = (status: InventoryStatusCode) => {
    switch (status) {
      case InventoryStatusCode.AVAILABLE_LIVE:
        return "#52c41a";
      case InventoryStatusCode.AVAILABLE_ON_HAND:
        return "#1890ff";
      case InventoryStatusCode.NO_POSSESSION:
        return "#fa8c16";
      case InventoryStatusCode.RELEASED:
        return "#722ed1";
      default:
        return "#d9d9d9";
    }
  };

  const statusOptions = [
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: getStatusColor(InventoryStatusCode.AVAILABLE_LIVE) }}
          />
          <span>Available Live</span>
        </div>
      ),
      value: InventoryStatusCode.AVAILABLE_LIVE,
    },
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: getStatusColor(InventoryStatusCode.AVAILABLE_ON_HAND) }}
          />
          <span>Available On Hand</span>
        </div>
      ),
      value: InventoryStatusCode.AVAILABLE_ON_HAND,
    },
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: getStatusColor(InventoryStatusCode.NO_POSSESSION) }}
          />
          <span>No Possession</span>
        </div>
      ),
      value: InventoryStatusCode.NO_POSSESSION,
    },
    {
      label: (
        <div className="flex items-center space-x-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: getStatusColor(InventoryStatusCode.RELEASED) }}
          />
          <span>Released</span>
        </div>
      ),
      value: InventoryStatusCode.RELEASED,
    },
  ];

  const itemCount = selectedInventories.length;

  return (
    <>
      <button
        onClick={handleOpenModal}
        disabled={selectedInventories.length === 0}
        className={`px-3 py-2 rounded-lg flex items-center space-x-1 ${
          selectedInventories.length === 0
            ? "border border-gray-200 text-gray-400 cursor-not-allowed"
            : "border border-gray-300 text-gray-700 hover:bg-gray-50"
        }`}
        title="Bulk update selected inventory"
      >
        <Settings size={16} />
        <span>Inventory Control</span>
      </button>

      <Modal
        title={
          <div className="flex items-center space-x-2">
            <Package className="text-blue-600" size={20} />
            <span>Bulk Update Inventory</span>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-2">
            <AlertCircle className="text-blue-600 mt-0.5" size={16} />
            <div>
              <p className="text-sm text-blue-800 font-medium">
                Updating {itemCount} inventory item{itemCount !== 1 ? "s" : ""}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Only the fields you specify will be updated. Leave fields empty to keep existing values.
              </p>
            </div>
          </div>
        </div>

        {/* Selected Items Preview */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Selected Items:</h4>
          <div className="max-h-32 overflow-y-auto border rounded-lg p-2 bg-gray-50">
            {selectedInventories.slice(0, 5).map((item) => (
              <div key={item.id} className="text-xs text-gray-600 py-1">
                {item.inventoryRef} - {item.title || "Untitled"}
              </div>
            ))}
            {selectedInventories.length > 5 && (
              <div className="text-xs text-gray-500 py-1">
                +{selectedInventories.length - 5} more items...
              </div>
            )}
          </div>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-gray-400" />
                <span>Status</span>
              </div>
            }
            name="status"
          >
            <Select
              placeholder="Select new status (optional)"
              allowClear
              options={statusOptions}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="flex items-center space-x-2">
                <MapPin className="text-gray-600" size={14} />
                <span>Location</span>
              </div>
            }
            name="locationId"
          >
            <Select
              placeholder="Select new location (optional)"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              <Select.Option value="">None (Clear Location)</Select.Option>
              {locations?.items.map((location: any) => (
                <Select.Option key={location.id} value={location.id}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Artist Name" name="artistName">
            <Input placeholder="Update artist name (optional)" />
          </Form.Item>

          <Form.Item name="swapWidthHeight" valuePropName="checked">
            <Checkbox>
              <span className="font-medium">Swap W and H</span>
              <span className="text-xs text-gray-500 ml-2">— swaps the Width and Height values for all selected items</span>
            </Checkbox>
          </Form.Item>

          <div className="grid grid-cols-3 gap-4">
            <Form.Item label="Length (in)" name="length">
              <Input type="number" placeholder="Length" step="0.1" />
            </Form.Item>

            <Form.Item label="Width (in)" name="width">
              <Input type="number" placeholder="Width" step="0.1" />
            </Form.Item>

            <Form.Item label="Height (in)" name="height">
              <Input type="number" placeholder="Height" step="0.1" />
            </Form.Item>
          </div>

          <Form.Item label="Packing Type" name="packingType">
            <Select
              placeholder="Select packing type (optional)"
              allowClear
              options={[
                { label: "Loose", value: "LOOSE" },
                { label: "Slipcase", value: "SLIPCASE" },
                { label: "CB Fold", value: "CB_FOLD" },
                { label: "Shadow Box", value: "SHADOW_BOX" },
                { label: "Floating Shadow Box", value: "FLOATING_SHADOW_BOX" },
                { label: "Poly Only", value: "POLY_ONLY" },
                { label: "Travel Frame", value: "TRAVEL_FRAME" },
                { label: "Crate", value: "CRATE" },
                { label: "Slat Crate", value: "SLATCRATE" },
                { label: "Pallet", value: "PALLET" },
                { label: "C-Bin", value: "CBIN" },
                { label: "Commercial Bin", value: "COMMERCIAL_BIN" },
                { label: "Unknown Packaging", value: "UNKNOWN_PACKAGING" },
                { label: "Box", value: "BOX" },
                { label: "Custom", value: "CUSTOM" },
                { label: "T-Frame", value: "T_Frame" },
                { label: "Blanket Wrapped", value: "BLANKET_WRAPPED" },
                { label: "Poly/Foam", value: "POLY_FOAM" },
                { label: "Poly/Foam/CB", value: "POLY_FOAM_CB" },
              ]}
            />
          </Form.Item>

          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              placeholder="Add notes about this update (optional)"
              rows={3}
            />
          </Form.Item>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Update {itemCount} Item{itemCount !== 1 ? "s" : ""}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
};
